import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LockDataProps, UserResponse } from 'types/api/user';
import { RootState } from '../store';

export interface UserState {
  adminId: string;
  avatar: string | null;
  email: string;
  id: string;
  normal_id?: number;
  loggedIn: boolean;
  name: string;
  role: string;
  showForgotPassword: boolean;
  showLogin: boolean;
  showRegister: boolean;
  raw?: UserResponse['data'];
  isGuest?: boolean;
  showUpdateUserName?: boolean;
  showCompanyDetail?: boolean;
  company_name?: string | null;
  isGodaddyGuest?: boolean;
  showGodaddyUserNameModal?: boolean;
  taskotifyUsers?: string[] | null;
  showLockFeature?: boolean;
  lockTitle: string;
  lockParagraph: string;
  lockImg: string;
  rank: number;
  signupOption: string;
  token?: string;
  authenticationFailed: boolean;
  additional_reviews: number;
  // New properties for enhanced functionality
  username?: string | null;
  first_name?: string | null;
  last_name?: string | null;
}

const initialState: UserState = {
  adminId: '',
  avatar: null,
  email: '',
  id: '',
  normal_id: undefined,
  loggedIn: false,
  name: '',
  role: '',
  showForgotPassword: false,
  showLogin: false,
  showRegister: false,
  raw: undefined,
  isGuest: false,
  showUpdateUserName: false,
  showCompanyDetail: false,
  company_name: null,
  isGodaddyGuest: false,
  showGodaddyUserNameModal: false,
  taskotifyUsers: null,
  showLockFeature: false,
  lockTitle: '',
  lockParagraph: '',
  lockImg: '',
  rank: 2,
  signupOption: '',
  token: undefined,
  authenticationFailed: false,
  additional_reviews: 0,
  username: null,
  first_name: null,
  last_name: null,
};

const userSlice = createSlice({
  initialState,
  name: 'user',
  reducers: {
    setLoggedIn: (state, action: PayloadAction<boolean>) => {
      state.loggedIn = action.payload;
    },
    setShowForgotPassword: (state, action: PayloadAction<boolean>) => {
      state.showForgotPassword = action.payload;
    },
    setShowLogin: (state, action: PayloadAction<boolean>) => {
      state.showLogin = action.payload;
    },
    setShowRegister: (state, action: PayloadAction<boolean>) => {
      state.showRegister = action.payload;
    },
    setSignupOption: (state, action: PayloadAction<string>) => {
      state.signupOption = action.payload;
    },
    setShowUpdateUserName: (state, action: PayloadAction<boolean>) => {
      state.showUpdateUserName = action.payload;
    },
    setUpdateUserName: (state, action: PayloadAction<string>) => {
      state.name = action.payload;
    },
    setShowCompanyDetail: (
      state,
      action: PayloadAction<{ open: boolean; companyName: string | null }>
    ) => {
      state.showCompanyDetail = action.payload.open;
      state.company_name = action.payload.companyName;
    },
    setOpenGodaddyGuestNameModal: (state, action: PayloadAction<boolean>) => {
      state.showGodaddyUserNameModal = action.payload;
    },
    setTaskotifyUsers: (state, action: PayloadAction<string[] | null>) => {
      state.taskotifyUsers = action.payload;
    },
    setShowLockFeature: (state, action: PayloadAction<LockDataProps>) => {
      state.showLockFeature = action.payload.showLockFeature;
      state.lockTitle = action.payload.lockTitle;
      state.lockParagraph = action.payload.lockParagraph;
      state.lockImg = action.payload.lockImg;
      state.rank = action.payload.rank;
    },
    setUserDetails: (state, action: PayloadAction<UserResponse>) => {
      state.adminId = action.payload.data.admin_id;
      state.email = action.payload.data.email;
      state.id = action.payload.data.id;
      state.name = action.payload.data.name;
      state.role = action.payload.data.role;
      state.normal_id = action.payload.data.normal_id;
      state.avatar = action.payload.data.avatar;
      state.raw = action.payload.data;
      state.isGuest = action.payload.data.isGuest;
      state.isGodaddyGuest = action.payload.data.isGodaddyGuest;
      state.additional_reviews = action.payload.data.additional_reviews;
      // Set new fields if available
      state.username = action.payload.data.username || null;
      state.first_name = action.payload.data.first_name || null;
      state.last_name = action.payload.data.last_name || null;
    },
    resetUserDetails: (state) => {
      state.adminId = '';
      state.email = '';
      state.id = '';
      state.name = '';
      state.role = '';
      state.normal_id = undefined;
      state.avatar = null;
      state.raw = undefined;
      state.isGuest = false;
      state.isGodaddyGuest = false;
      state.additional_reviews = 0;
      state.username = null;
      state.first_name = null;
      state.last_name = null;
    },
    setToken: (state, action: PayloadAction<string | undefined>) => {
      state.token = action.payload;
    },
    setAuthenticationFailed: (state, action: PayloadAction<boolean>) => {
      state.authenticationFailed = action.payload;
    },
    logout: () => {
      // This action will be intercepted by the rootReducer in store.ts
      // which will dispatch the LOGOUT action type to clear all state
      return initialState;
    },
  },
});

export const {
  setLoggedIn,
  setShowForgotPassword,
  setShowLogin,
  setSignupOption,
  setShowRegister,
  setShowUpdateUserName,
  setUserDetails,
  setUpdateUserName,
  setShowCompanyDetail,
  setOpenGodaddyGuestNameModal,
  setTaskotifyUsers,
  setShowLockFeature,
  setToken,
  resetUserDetails,
  setAuthenticationFailed,
  logout,
} = userSlice.actions;

export const getLoggedIn = (state: RootState) => state.user.loggedIn;
export const getShowForgotPassword = (state: RootState) =>
  state.user.showForgotPassword;
export const getShowLogin = (state: RootState) => state.user.showLogin;
export const getShowRegister = (state: RootState) => state.user.showRegister;
export const getSignUpOption = (state: RootState) => state.user.signupOption;
export const getUser = (state: RootState) => state.user;
export const getShowUpdateUserName = (state: RootState) =>
  state.user.showUpdateUserName;
export const getShowCompanyDetail = (state: RootState) =>
  state.user.showCompanyDetail;
export const getshowGodaddyUserNameModal = (state: RootState) =>
  state.user.showGodaddyUserNameModal;
export const getTaskotifyUsers = (state: RootState) =>
  state.user.taskotifyUsers;
export const getShowLockFeature = (state: RootState) =>
  state.user.showLockFeature;
export const getAuthToken = (state: RootState) => state.user.token;
export const getAuthenticationFailed = (state: RootState) =>
  state.user.authenticationFailed;
// Backward compatibility for typo in selector name
export const getAutheticationFailed = (state: RootState) =>
  state.user.authenticationFailed;

export default userSlice.reducer;
